import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HELP_OPTIONS, HELP_OPTION_CODES, ROUTING_CODES, DOC_CHECK_FORM, REGIONS_SUPPORTED, HELP_OPTIONS_, ALERT_MODES, ENVIRONMENT, DDActions } from 'src/app/_constants/app.constants';
import { BoardingPassService } from 'src/app/_services/api/boardingpass/boardingpass.service';
import { ObjectLiteral } from 'src/app/_models/user';
import { Subscription } from 'rxjs';
import { AlertMsg } from 'src/app/_models/common';
import { CryptoService } from 'src/app/_services/crypto/crypto.service';
import { DOCUMENT } from '@angular/common';
import { AnalyticsService } from 'src/app/_services/sdk/analytics/analytics.service';
import { HelpOptionsService } from "src/app/_services/api/help-options/help-options.service";
import { FlightService } from 'src/app/_services/api/flight/flight.service';
import { LanguageService } from 'src/app/_services/api/language/language.service';
import { LoggerService } from 'src/app/_services/ui/logger/logger.service';
import { SessionService } from 'src/app/_services/session/session.service';

@Component({
  selector: 'ua-help-options-form',
  templateUrl: './help-options-form.component.html',
  styleUrls: ['./help-options-form.component.scss']
})
export class HelpOptionsFormComponent implements OnInit, OnDestroy {

  @Input() title: any = HELP_OPTIONS.TITLE;

  helpOptions = [];
  secondaryOption = HELP_OPTIONS.SECONDARY;
  docCheckRegions = DOC_CHECK_FORM.CONTROLS.SELECTED_REGION.options.map(data => data.value);
  showSendBoardingPass = false;
  showBoardingPass = false;
  showAlertMessage = false;
  alertMsg: AlertMsg = { message: "", mode: "", title: "" };
  languageSubscription: Subscription;
  pageConstants: ObjectLiteral;
  showRebookConfirmationModal: boolean = false;
  redirectURL: string;
  window: Window;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private logger: LoggerService,
    private router: Router,
    private sessionService: SessionService,
    private flightService: FlightService,
    private cryptoService: CryptoService,
    private boardingPassService: BoardingPassService,
    private languageService: LanguageService,
    private helpOptionsService: HelpOptionsService,
    private analyticsService: AnalyticsService,
  ) {
    this.window = this.document.defaultView;
    this.languageSubscription = this.languageService.languageChanges.subscribe(() => {
      this.pageConstants = this.languageService.constants?.helpOptions || {};
    })
  }

  ngOnInit(): void {
    this.setHelpOptions();
  }

  /**
   * sets up the buttons on page relevant to the scenarios.
   */

  setHelpOptions() {
    this.helpOptionsService.helpOptions.options.forEach((option) => {
      this.helpOptions.push(HELP_OPTIONS_[option]);
    })
    this.secondaryOption.display = this.helpOptionsService.helpOptions.secondary ? true : false;
  }

  async onOptionSelect(value: string) {
    this.logger.debug("HelpOptionsComponent.onOptionsSelect", value);
    this.analyticsService.addAction(DDActions.HELP_OPTION_SELECTED, { selectedHelpOption: value })
    this.sessionService.setReason(value);
    this.showAlertMessage = false;

    let docCheckRegion = this.flightService.flightDetails?.docCheckRegion?.toLowerCase() || '';

    if (value == HELP_OPTION_CODES.ARRANGE_DELIVERY) {

      let { pnr_no, last_name } = this.sessionService.pnrDetails;
      let bagTag = this.flightService.getDelayedBag();
      if (pnr_no && last_name && bagTag) {
        let url = `${ENVIRONMENT.PROPERTIES.EBSO_URL}/?data=${encodeURIComponent(this.cryptoService.encrypt({
          pnr: pnr_no,
          lname: last_name,
          bagNo: bagTag
        }, ENVIRONMENT.PROPERTIES.EBSO_CLIENT_SECRET))}`;
        this.logger.debug("HelpOptionsComponent.onOptionsSelect.HELP_OPTION_CODES.ARRANGE_DELIVERY", url);
        this.window.open(encodeURI(url), '_blank');
      } else {
        this.configForwardRoute(ROUTING_CODES.PRE_CHAT.ROUTE);
      }
      return;
    }
    if (value == HELP_OPTION_CODES.BOARDING_PASS) {
      await this.openBoardingPassViewer();
      return;
    }

    if (value == HELP_OPTION_CODES.SEND_BOARDING_PASS) {
      await this.openSendBoardingPassModal();
      return;
    }

    if (value == HELP_OPTION_CODES.FLIGHT_REBOOK && this.flightService.flightDetails.canRebookFlight) {
      this.configForwardRoute(ROUTING_CODES.FLIGHT_REBOOK.ROUTE);
      return;
    }

    if (value == HELP_OPTION_CODES.FLIGHT_CHANGE && this.flightService.flightDetails.canChangeFlight) {
      this.configForwardRoute(ROUTING_CODES.FLIGHT_REBOOK.ROUTE);
      return;
    }

    if (value == HELP_OPTION_CODES.DOC_CHECK && (docCheckRegion == '' || !this.docCheckRegions.includes(docCheckRegion))) {
      this.sessionService.locationData["routing-code"] = ROUTING_CODES.DOC.CODE;
      this.sessionService.locationData.region = REGIONS_SUPPORTED.OTHER;
      this.configForwardRoute(ROUTING_CODES.PRE_CHAT.ROUTE);

    } else if (value == HELP_OPTION_CODES.DOC_CHECK && this.docCheckRegions.includes(docCheckRegion)) {
      this.sessionService.locationData["routing-code"] = ROUTING_CODES.DOC.CODE;
      this.sessionService.locationData.region = docCheckRegion;
      this.configForwardRoute(ROUTING_CODES.PRE_CHAT.ROUTE);

    } else {
      this.configForwardRoute(ROUTING_CODES.PRE_CHAT.ROUTE);
    }

  }

  /**
   * Configure what screen to show next
   */
  configForwardRoute(route: string) {
    this.logger.debug("HelpOptionsComonent.configForwardRoute", route, this);
    //In case of irrop pnr, route user to self-service page
    this.flightService.isIrropRoute() ? this.redirectToSelfService() : this.router.navigate([route], { replaceUrl: true });
  }

  redirectToSelfService() {
    this.logger.debug('HelpOptionsFormComponent : configForwardRoute , it is IrropRoute - routing to self-service page');
    this.router.navigate([ROUTING_CODES.SELF_SERVICE.ROUTE], { replaceUrl: true });
  }

  async openBoardingPassViewer() {
    try {
      await this.boardingPassService.getBoardingPassDetails({
        pnr: this.sessionService.pnrDetails.pnr_no,
        LastName: this.sessionService.pnrDetails.last_name
      });
      if (this.boardingPassService.boardingPassDetails?.segmentList.length) {
        this.showBoardingPass = true;
      }
    } catch (e) {
      this.logger.error("HelpOptionsFormComponent: openBoardingPassViewer()", e);
      this.showAlertMessage = true;
      this.alertMsg.mode = ALERT_MODES.ERROR;
      this.alertMsg.message = this.pageConstants[HELP_OPTIONS.ERROR_MESSAGES.BOARDING_PASS];
    }
  }

  openSendBoardingPassModal() {
    this.showSendBoardingPass = true;
  }

  closeModal(event?) {
    if (event == ALERT_MODES.SUCCESS) {
      this.showAlertMessage = true;
      this.alertMsg.mode = ALERT_MODES.SUCCESS;
      this.alertMsg.message = this.pageConstants[HELP_OPTIONS.SUCCESS_MESSAGES.SENT_BOARDING_PASS]
    }
    this.showBoardingPass = false;
    this.showSendBoardingPass = false;
  }

  /**
   * ngOnDestroy
   */
  ngOnDestroy() {
    this.flightService.showBoardingPass = false;
    this.languageSubscription && this.languageSubscription.unsubscribe();
  }
}
